import { Color, Spacing } from '@persona-id/design-tokens';
import { MailIcon, XIcon } from '@persona-id/design-tokens/icons';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { UserNotificationStatus } from 'dashboard/models/user-notification';
import { UserNotificationsDetails } from 'dashboard/redux/slices/user-notifications';
import { Z_INDICES } from 'dashboard/styles/variables';
import {
  ARCHIVED_REQUEST_KEY,
  INBOX_REQUEST_KEY,
  RequestKey,
} from 'dashboard/views/DashboardCasesView/constants';
import NotificationBell from 'dashboard/views/DashboardCasesView/NotificationBell';
import NullState from 'dashboard/views/DashboardCasesView/NullState';
import UserNotificationsList from 'dashboard/views/DashboardCasesView/UserNotificationsList';
import Badge from 'design-system/components/Badge';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import { Tab, TabContainer } from 'design-system/components/TabBar';

const StyledCard = styled(Card)`
  max-height: 600px;
  width: 330px;
`;

const HeaderContainer = styled('div')`
  padding: 0px ${Spacing[3]};

  display: flex;
  justify-content: space-between;

  border-bottom: 1px solid ${Color.BorderHairline};
`;

const ContentContainer = styled('div')`
  overflow: auto;
`;

const FooterContainer = styled('div')`
  border-top: 1px solid ${Color.BorderHairline};
  padding: 10px ${Spacing[3]};
`;

const MarkAllAsReadButton = styled(Button)`
  max-width: 110px;
`;

export interface UserNotificationsProps {
  userNotifications: {
    [INBOX_REQUEST_KEY]: UserNotificationsDetails | undefined;
    [ARCHIVED_REQUEST_KEY]: UserNotificationsDetails | undefined;
  };
  handleMarkAllAsReceived: () => void;
  handleArchive: (notificationId: string) => void;
  handleUnarchive: (notificationId: string) => void;
  handleMarkAllAsArchived: () => void;
  loadUserNotificationPage: (
    pageNumber: number,
    status: UserNotificationStatus,
    requestKey: RequestKey,
  ) => Promise<void>;
  trackViewUserNotificationMetrics?: () => void;
}

const UserNotifications = ({
  userNotifications,
  handleMarkAllAsReceived,
  handleArchive,
  handleUnarchive,
  handleMarkAllAsArchived,
  loadUserNotificationPage,
  trackViewUserNotificationMetrics,
}: UserNotificationsProps) => {
  const [selected, setSelected] = useState(0);

  // TODO (Amy): user notifications are limited to 300 at a time so we should at least show an error message if the user tries to load more notifications
  const inboxNotifications = userNotifications[INBOX_REQUEST_KEY]?.userNotifications ?? [];
  const archivedNotifications = userNotifications[ARCHIVED_REQUEST_KEY]?.userNotifications ?? [];

  const inboxNotificationsCount =
    userNotifications[INBOX_REQUEST_KEY]?.paginationParams?.['total-count'] ?? 0;

  const tabsConfig = useMemo(
    () => [
      {
        name: 'Inbox',
        badge: <Badge>{inboxNotificationsCount > 99 ? '99+' : inboxNotificationsCount}</Badge>,
        contents: (
          <>
            <ContentContainer id='inbox-notifications-container'>
              <UserNotificationsList
                containerId='inbox-notifications-container'
                userNotifications={inboxNotifications}
                userNotificationsPaginationParams={
                  userNotifications[INBOX_REQUEST_KEY]?.paginationParams
                }
                nullState={
                  <NullState
                    header="You're all caught up"
                    description="You'll receive notifications when cases are assigned to you or when you're tagged in comments"
                  />
                }
                actionIcon={<XIcon />}
                actionTooltipText='Archive'
                handleAction={handleArchive}
                loadUserNotificationPage={(pageNumber: number) =>
                  loadUserNotificationPage(
                    pageNumber,
                    UserNotificationStatus.Delivered,
                    INBOX_REQUEST_KEY,
                  )
                }
              />
            </ContentContainer>
            {inboxNotifications.length > 0 && (
              <FooterContainer>
                <MarkAllAsReadButton
                  id='user-notifications__mark-all-as-read'
                  variant='basic'
                  autoFocus={false}
                  onClick={handleMarkAllAsArchived}
                >
                  Archive all
                </MarkAllAsReadButton>
              </FooterContainer>
            )}
          </>
        ),
      },
      {
        name: 'Archived',
        badge: undefined,
        contents: (
          <ContentContainer id='archived-notifications-container'>
            <UserNotificationsList
              containerId='archived-notifications-container'
              userNotifications={archivedNotifications}
              userNotificationsPaginationParams={
                userNotifications[ARCHIVED_REQUEST_KEY]?.paginationParams
              }
              nullState={
                <NullState
                  header='No archived updates'
                  description='Any inbox updates you archive will show up here.'
                />
              }
              actionIcon={<MailIcon />}
              actionTooltipText='Mark as unread'
              handleAction={handleUnarchive}
              loadUserNotificationPage={(pageNumber: number) =>
                loadUserNotificationPage(
                  pageNumber,
                  UserNotificationStatus.Archived,
                  ARCHIVED_REQUEST_KEY,
                )
              }
            />
          </ContentContainer>
        ),
      },
    ],
    [
      inboxNotificationsCount,
      inboxNotifications,
      archivedNotifications,
      handleArchive,
      handleUnarchive,
      handleMarkAllAsArchived,
    ],
  );

  return (
    <Popover
      trigger={
        <Button
          id='user_notifications__trigger'
          variant='inverse'
          width='icon'
          onClick={() => {
            handleMarkAllAsReceived();
            if (trackViewUserNotificationMetrics) {
              trackViewUserNotificationMetrics();
            }
          }}
        >
          <NotificationBell unreadCount={inboxNotificationsCount} />
        </Button>
      }
    >
      <Popover.Content style={{ zIndex: Z_INDICES.topNavTooltip }} side='bottom' align='end'>
        <StyledCard elevated>
          <HeaderContainer>
            <TabContainer>
              {tabsConfig.map((tabConfig, idx) => (
                <Tab
                  id={`tab-${tabConfig.name}`}
                  key={`tab-${tabConfig.name}`}
                  selected={selected === idx}
                  badge={tabConfig.badge}
                  onClick={() => setSelected(idx)}
                >
                  {tabConfig.name}
                </Tab>
              ))}
            </TabContainer>
          </HeaderContainer>

          {tabsConfig[selected].contents}
        </StyledCard>
      </Popover.Content>
    </Popover>
  );
};

export default UserNotifications;
